export const POPOVER_MAX_WIDTH = 660
export const POPOVER_MAX_HEIGHT = 600

const VIEWPORT_PADDING = 20
const TRIGGER_GAP = 18

export type PopoverLayout = {
  top: number
  left: number
  width: number
  height: number
}

/**
 * Where the filter-selection popover goes, and how big it is, for a given
 * trigger and viewport. `position: fixed` coordinates, so the same numbers as
 * `getBoundingClientRect()` — no scroll offsets.
 *
 * The box is at most 660x600 but shrinks with the viewport, and is always kept
 * fully on screen:
 * - 'center' centers in the viewport (not on the trigger: different triggers
 *   for one popover would otherwise put it in different places).
 * - 'right' anchors off the trigger's right edge, then is pulled back so it
 *   never runs past the right edge. That happens for a trigger inside a
 *   right-hand drawer, where there is no room to the trigger's right.
 */
export function getPopoverLayout({
  trigger,
  anchorMode,
  viewportWidth,
  viewportHeight,
}: {
  trigger: { top: number; height: number; right: number }
  anchorMode: 'right' | 'center'
  viewportWidth: number
  viewportHeight: number
}): PopoverLayout {
  const width = Math.max(
    0,
    Math.min(POPOVER_MAX_WIDTH, viewportWidth - 2 * VIEWPORT_PADDING),
  )
  const height = Math.max(
    0,
    Math.min(POPOVER_MAX_HEIGHT, viewportHeight - 2 * VIEWPORT_PADDING),
  )

  let top = trigger.top + trigger.height / 2 - height / 2 - 160
  if (top + height > viewportHeight - VIEWPORT_PADDING) {
    top = viewportHeight - height - VIEWPORT_PADDING
  }
  if (top < VIEWPORT_PADDING) {
    top = VIEWPORT_PADDING
  }

  let left =
    anchorMode === 'center'
      ? (viewportWidth - width) / 2
      : trigger.right + TRIGGER_GAP
  if (left + width > viewportWidth - VIEWPORT_PADDING) {
    left = viewportWidth - width - VIEWPORT_PADDING
  }
  if (left < VIEWPORT_PADDING) {
    left = VIEWPORT_PADDING
  }

  return { top, left, width, height }
}
